import { useEffect, useState } from 'react';

import { pickNewerSiteNavigation } from '@lib/navigation/site-navigation';
import { SITE_NAVIGATION_API_PATH } from '@lib/navigation/types';
import type { SiteNavigation } from '@lib/navigation/types';

/**
 * The newest menus seen this visit. Only the server reads them into page
 * props, so a client-side navigation can land on props without them, and a
 * page built once at deploy time carries that deploy's copy. The browser also
 * asks the edge-cached route once per visit so those pages catch up.
 */
export const useLatestSiteNavigation = (
  siteNavigation: SiteNavigation | null | undefined
): SiteNavigation | null => {
  const [latest, setLatest] = useState<SiteNavigation | null>(
    siteNavigation ?? null
  );

  useEffect(() => {
    if (siteNavigation) {
      setLatest((current) => pickNewerSiteNavigation(current, siteNavigation));
    }
  }, [siteNavigation]);

  useEffect(() => {
    const controller = new AbortController();
    fetch(SITE_NAVIGATION_API_PATH, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((fetched: SiteNavigation | null) => {
        if (fetched) {
          setLatest((current) => pickNewerSiteNavigation(current, fetched));
        }
      })
      // The menus from the page props still render.
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  return pickNewerSiteNavigation(latest, siteNavigation ?? null);
};
